import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, InputGroup } from 'react-bootstrap';
import '../styles/LocationSearchBar.css';

const RECENT_KEY = 'recent_searches';

const LocationSearchBar = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [location, setLocation] = useState('');
    const [locationCoords, setLocationCoords] = useState(null); 
    const [dropdownResults, setDropdownResults] = useState({ restaurants: [], groceryStores: [], cloudKitchens: [], items: [] });
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [recent, setRecent] = useState([]);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [geocoderService, setGeocoderService] = useState(null); 
    const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false); 
    const [isLoading, setIsLoading] = useState(false); // Add loading state

    const inputRef = useRef(); 
    const locationInputRef = useRef(); 
    const dropdownRef = useRef(); 
    const searchWrapperRef = useRef();

    const navigate = useNavigate();

    useEffect(() => {
        // Check if Google Maps API is already loaded
        if (window.google && window.google.maps && window.google.maps.Geocoder) {
            setIsGoogleMapsLoaded(true);
            try {
                setGeocoderService(new window.google.maps.Geocoder());
            } catch (error) {
                console.warn("Failed to initialize Geocoder:", error);
            }
            return;
        }

        // If not loaded, add a retry mechanism
        let retries = 0;
        const maxRetries = 30; // 3 seconds max
        const checkInterval = setInterval(() => {
            if (window.google && window.google.maps && window.google.maps.Geocoder) {
                clearInterval(checkInterval);
                setIsGoogleMapsLoaded(true);
                try {
                    setGeocoderService(new window.google.maps.Geocoder());
                } catch (error) {
                    console.warn("Failed to initialize Geocoder:", error);
                }
            } else if (retries >= maxRetries) {
                clearInterval(checkInterval);
                console.warn("Google Maps Geocoder not available after retries. Address search may not work properly.");
                setIsGoogleMapsLoaded(false);
            }
            retries++;
        }, 100);

        return () => clearInterval(checkInterval);
    }, []); 

    async function geocodeEnteredLocation() {
        if (!location || !isGoogleMapsLoaded || !geocoderService) { 
            setLocationCoords(null);
            return;
        }

        try {
            const response = await geocoderService.geocode({ address: location });
            if (response.results && response.results.length > 0) {
                const { lat, lng } = response.results[0].geometry.location;
                setLocationCoords({ lat: lat(), lng: lng() });
                setLocation(response.results[0].formatted_address); 
            } else {
                setLocationCoords(null);
            }
        } catch (error) {
            console.error("Geocoding failed:", error);
            setLocationCoords(null);
        }
    }

    useEffect(() => {
        const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
        setRecent(stored);
    }, []);

    const debouncedSearch = useCallback(() => {
        if (!searchQuery || searchQuery.trim() === '') {
            setDropdownResults({ restaurants: [], groceryStores: [], cloudKitchens: [], items: [] });
            setDropdownOpen(false);
            setIsLoading(false);
            return;
        }
        
        setIsLoading(true);
        const timeout = setTimeout(() => {
            fetch(`/api/search?q=${encodeURIComponent(searchQuery)}`)
                .then(res => res.json())
                .then(data => {
                    setDropdownResults(data);
                    // Only open dropdown if there are results
                    const hasResults = data.restaurants?.length > 0 || 
                                     data.groceryStores?.length > 0 || 
                                     data.cloudKitchens?.length > 0 || 
                                     data.items?.length > 0;
                    setDropdownOpen(hasResults);
                    setIsLoading(false);
                })
                .catch(() => {
                    setDropdownResults({ restaurants: [], groceryStores: [], cloudKitchens: [], items: [] });
                    setDropdownOpen(false);
                    setIsLoading(false);
                });
        }, 300);
        return () => clearTimeout(timeout);
    }, [searchQuery]);

    useEffect(() => {
        debouncedSearch();
    }, [debouncedSearch]);

    const allDropdownItems = useMemo(() => {
        const sections = ['restaurants', 'groceryStores', 'cloudKitchens', 'items'];
        return sections.flatMap(section =>
            dropdownResults[section]?.map(item => ({
                section,
                id: item._id || item.id || item.itemId,
                name: item.name,
                image: item.image,
                type: section === 'items' ? 'item' : section.replace('Stores', '').toLowerCase(),
                description: item.description 
            })) || []
        );
    }, [dropdownResults]);

    const addRecent = (text) => {
        if (!text || text.trim() === '') return;
        let updated = [text, ...recent.filter(r => r !== text)].slice(0, 5);
        setRecent(updated);
        localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
    };

    const deleteRecent = (text) => {
        let updated = recent.filter(r => r !== text);
        setRecent(updated);
        localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
    };

    const handleResultClick = (type, id, itemObj) => {
        addRecent(searchQuery);
        setDropdownOpen(false);
        setSearchQuery('');
        setFocusedIndex(-1);
        if (type === 'restaurant') navigate(`/restaurants/${id}`, { state: { showMenu: true } });
        else if (type === 'grocery') navigate(`/grocery-stores/${id}`, { state: { showMenu: true } });
        else if (type === 'cloudkitchen') navigate(`/cloudkitchens/${id}`, { state: { showMenu: true } });
        else if (type === 'item' && itemObj) {
            navigate('/search-results', { state: { itemName: itemObj.name || searchQuery } });
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (focusedIndex >= 0 && allDropdownItems[focusedIndex]) {
                const { type, id, name } = allDropdownItems[focusedIndex];
                handleResultClick(type, id, { name });
            } else if (searchQuery) {
                handleSearch(e);
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (allDropdownItems.length > 0) {
                setFocusedIndex(prev => Math.min(prev + 1, allDropdownItems.length - 1));
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex(prev => Math.max(prev - 1, -1));
        } else if (e.key === 'Escape') {
            setDropdownOpen(false);
            setFocusedIndex(-1);
        }
    };

    useEffect(() => {
        const handleClick = (e) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target) &&
                searchWrapperRef.current &&
                !searchWrapperRef.current.contains(e.target) &&
                locationInputRef.current &&
                !locationInputRef.current.contains(e.target)
            ) {
                setDropdownOpen(false);
                setFocusedIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => {
            document.removeEventListener('mousedown', handleClick);
        };
    }, []);

    const handleLocationChange = (e) => {
        setLocation(e.target.value);
        setLocationCoords(null);
    };

    const handleLocationBlur = () => {
        if (location && !locationCoords) {
            geocodeEnteredLocation();
        }
    };

    const handleLocationKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (location && !locationCoords) {
                geocodeEnteredLocation();
            }
            inputRef.current?.focus();
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();

        let finalLocationCoords = locationCoords;

        if (location && !locationCoords && isGoogleMapsLoaded && geocoderService) {
            try {
                const response = await geocoderService.geocode({ address: location });
                if (response.results && response.results.length > 0) {
                    const { lat, lng } = response.results[0].geometry.location;
                    finalLocationCoords = { lat: lat(), lng: lng() };
                    setLocation(response.results[0].formatted_address);
                } else {
                    alert('Could not find the entered location. Please be more specific.');
                    return;
                }
            } catch (error) {
                console.error("Geocoding failed on search:", error);
                alert('Failed to process location. Please try again.');
                return;
            }
        }

        if (searchQuery || finalLocationCoords) {
            addRecent(searchQuery);
            setDropdownOpen(false);

            navigate('/search-results', {
                state: {
                    query: searchQuery,
                    location: location,
                    latitude: finalLocationCoords?.lat,
                    longitude: finalLocationCoords?.lng,
                    radius: 10
                }
            });
        }
    };

    const handleInputFocus = () => {
        // Only show recent searches if there's no search query and we have recent items
        if (!searchQuery && recent.length > 0) {
            setDropdownOpen(true);
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        if (value.trim() === '') {
            // If empty, show recent searches
            setDropdownOpen(recent.length > 0);
            setDropdownResults({ restaurants: [], groceryStores: [], cloudKitchens: [], items: [] });
        }
    };

    return (
        <div className="search-container">
            <Form onSubmit={handleSearch} className="search-bar-form" autoComplete="off">
                <div className="search-input-wrapper" ref={searchWrapperRef}>
                    <InputGroup className="search-group">
                        <Form.Control
                            ref={inputRef}
                            type="text"
                            placeholder="Search for food or Restaurants or Groceries..."
                            value={searchQuery}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onKeyDown={handleKeyDown}
                            className="search-input"
                            autoComplete="off"
                            aria-autocomplete="list"
                            aria-controls="search-dropdown"
                            aria-expanded={dropdownOpen}
                        />
                        <Button variant="primary" type="submit" aria-label="Search">
                            <i className="bi bi-search"></i>
                        </Button>
                    </InputGroup>
                    {dropdownOpen && (
                        <div
                            id="search-dropdown"
                            className="searchbar-dropdown"
                            ref={dropdownRef}
                            role="listbox"
                        >
                            {/* Show loading indicator */}
                            {isLoading && (
                                <div className="loading-results">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <span>Searching...</span>
                                </div>
                            )}
                            
                            {/* Show recent searches when no query */}
                            {!isLoading && searchQuery === '' && recent.length > 0 && (
                                <div className="recent-searches">
                                    <div className="recent-title">Recent Searches</div>
                                    {recent.map((r, index) => (
                                        <div
                                            key={r}
                                            className={`recent-item ${focusedIndex === index ? 'focused' : ''}`}
                                            onClick={() => { 
                                                setSearchQuery(r); 
                                                setDropdownOpen(true);
                                                inputRef.current?.focus();
                                            }}
                                            onKeyDown={(e) => e.key === 'Enter' && setSearchQuery(r)}
                                            role="option"
                                            aria-selected={focusedIndex === index}
                                        >
                                            <span>{r}</span>
                                            <span
                                                className="recent-delete"
                                                onClick={(e) => { e.stopPropagation(); deleteRecent(r); }}
                                                aria-label={`Remove ${r} from recent searches`}
                                            >
                                                ✕
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* Show no results message */}
                            {!isLoading && searchQuery && allDropdownItems.length === 0 && (
                                <div className="no-results">No results found for "{searchQuery}"</div>
                            )}
                            
                            {/* Show search results */}
                            {!isLoading && searchQuery && allDropdownItems.length > 0 && (
                                <React.Fragment>
                                    {['restaurants', 'groceryStores', 'cloudKitchens'].map(section => {
                                        let type = '';
                                        if (section === 'restaurants') type = 'restaurant';
                                        else if (section === 'groceryStores') type = 'grocery';
                                        else if (section === 'cloudKitchens') type = 'cloudkitchen';
                                        if (!dropdownResults[section]?.length) return null;
                                        return (
                                            <div key={section} className="dropdown-section">
                                                <div className="dropdown-section-title">
                                                    {section === 'groceryStores' ? 'Grocery Stores' : 
                                                     section === 'cloudKitchens' ? 'Cloud Kitchens' : 
                                                     section.charAt(0).toUpperCase() + section.slice(1)}
                                                </div>
                                                {dropdownResults[section].map((res) => {
                                                    const itemIndex = allDropdownItems.findIndex(i => i.id === (res._id || res.id) && i.section === section);
                                                    return (
                                                        <div
                                                            key={res._id || res.id}
                                                            className={`dropdown-result ${focusedIndex === itemIndex ? 'focused' : ''}`}
                                                            onClick={() => handleResultClick(type, res._id || res.id)}
                                                            onMouseEnter={() => setFocusedIndex(itemIndex)}
                                                            role="option"
                                                            aria-selected={focusedIndex === itemIndex}
                                                        >
                                                            <img
                                                                src={res.image || 'https://placehold.co/50x50/E0E0E0/333333?text=Img'}
                                                                alt={res.name}
                                                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/50x50/E0E0E0/333333?text=Img'; }}
                                                            />
                                                            <div>
                                                                <span>{res.name}</span>
                                                                {res.description && <small>{res.description}</small>}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                    {dropdownResults.items?.length > 0 && (
                                        <div className="dropdown-section">
                                            <div className="dropdown-section-title">Items</div>
                                            {Array.from(new Map(dropdownResults.items.map(item => [item.name?.toLowerCase(), item])).values()).map((item) => {
                                                const itemIndex = allDropdownItems.findIndex(i => i.id === item.itemId && i.section === 'items');
                                                return (
                                                    <div
                                                        key={item.itemId}
                                                        className={`dropdown-result ${focusedIndex === itemIndex ? 'focused' : ''}`}
                                                        onClick={() => handleResultClick('item', null, { name: item.name })}
                                                        onMouseEnter={() => setFocusedIndex(itemIndex)}
                                                        role="option"
                                                        aria-selected={focusedIndex === itemIndex}
                                                    >
                                                        <img
                                                            src={item.image || 'https://placehold.co/50x50/E0E0E0/333333?text=Img'}
                                                            alt={item.name}
                                                            onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/50x50/E0E0E0/333333?text=Img'; }}
                                                        />
                                                        <div>
                                                            <span>{item.name || 'Unnamed Dish'}</span>
                                                            {item.description && <small>{item.description}</small>}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                    )}
                </div>
                <div className="location-wrapper">
                    <InputGroup className="location-group">
                        <InputGroup.Text>
                            <i className="bi bi-geo-alt-fill"></i>
                        </InputGroup.Text>
                        <Form.Control
                            ref={locationInputRef}
                            type="text"
                            value={location}
                            onChange={handleLocationChange}
                            onBlur={handleLocationBlur}
                            onKeyDown={handleLocationKeyDown}
                            placeholder="Enter your delivery location..."
                            className="location-input"
                            autoComplete="off"
                        />
                    </InputGroup>
                </div>
            </Form>
        </div>
    );
};

export default LocationSearchBar;
